try {
    if (process.env.TOKENS_FEATURE_FLAG === 'true' || process.env.STORYBOOK_TOKENS_FEATURE_FLAG !== 'false') require('./index-new.css');
    else require('./index.css');
  } catch (e) {
    if (e.code !== 'MODULE_NOT_FOUND') {
      throw e; // Re-throw unexpected errors
    }
    console.warn('index.css / index-new.css not found, proceeding without it.');
  }


import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Typography } from '@design-system/typography';
import { useCurrentVariantStateContext } from '@phx-husky/current-variant-state';
import classNames from 'classnames';
import { ExtendableImage } from '@design-system/extendable-image';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
function __rest(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

const SelectorButton = forwardRef((_a, ref) => {
    var { className, href, isSelected, tooltip, fitable, onClick, children } = _a, htmlAttributes = __rest(_a, ["className", "href", "isSelected", "tooltip", "fitable", "onClick", "children"]);
    const [show, setShow] = useState(false);
    const newTokenisation = process.env.TOKENS_FEATURE_FLAG === 'true';
    const commonProps = Object.assign({ className: classNames(className, 'cds-selector-button', {
            'cds-selector-button__btn': !href && newTokenisation,
            'cds-selector-button__link': href && newTokenisation,
            'cds-link cds-link--selector': !newTokenisation,
            selected: isSelected,
            active: isSelected && !newTokenisation,
            fitable,
        }) }, htmlAttributes);
    if (href) {
        return tooltip ? (jsxs("div", Object.assign({ className: "cds-selector-container" }, { children: [jsx("a", Object.assign({ href: href, ref: ref, onMouseEnter: () => setShow(true), onMouseLeave: () => setShow(false) }, commonProps, { children: children })), jsxs("div", Object.assign({ className: classNames('cds-selector-modal', { show }) }, { children: [jsx("span", { children: tooltip }), jsx("div", { className: "cds-selector-modal-arrow" })] }))] }))) : (jsx("a", Object.assign({ href: href, ref: ref }, commonProps, { children: children })));
    }
    return (jsx("button", Object.assign({ type: "button", onClick: onClick }, commonProps, { children: children })));
});

const MultiVariantSelectorList = forwardRef(({ variants, productDataSku, label }, ref) => {
    const { setVariant, setVariantList, setInitialSku, currentVariant } = useCurrentVariantStateContext();
    useEffect(() => {
        setInitialSku(productDataSku);
        setVariantList(variants);
    }, [variants]);
    const isVariantSelected = (variantId) => variantId === currentVariant.variantId;
    return (jsx("div", Object.assign({ className: "cds-variant-selector", ref: ref }, { children: jsxs("div", Object.assign({ className: "cds-variant-selector__block cds-variant-selector__block--one-dimension" }, { children: [jsx(Typography, { id: "dimension1", content: label, variant: "body", size: "s", tag: "p" }), jsx("ul", Object.assign({ "aria-labelledby": "multi-variant-list", className: "cds-variant-selector__list" }, { children: variants.map(({ title, variantId }) => {
                        const isSelected = isVariantSelected(variantId);
                        return (jsx("li", { children: jsx(SelectorButton, Object.assign({ isSelected: isSelected, onClick: () => {
                                    setVariant(variantId);
                                } }, { children: title })) }, title));
                    }) }))] })) })));
});

const checkIsFallBack = (variants) => {
    const fallBackObj = variants.reduce((prev, current) => {
        var _a, _b;
        const { dimension1, dimension2 } = current;
        const dimension1Key = (_a = dimension1.variantName) === null || _a === void 0 ? void 0 : _a.toLowerCase();
        const dimension2Key = (_b = dimension2 === null || dimension2 === void 0 ? void 0 : dimension2.variantName) === null || _b === void 0 ? void 0 : _b.toLowerCase();
        return Object.assign(Object.assign({}, prev), { [dimension2Key]: Object.assign(Object.assign({}, prev[dimension2Key]), { [dimension1Key]: true }) });
    }, {});
    const fallBackArr = Object.keys(fallBackObj).map((key) => {
        const restDimensions = Object.keys(fallBackObj).reduce((prev, current) => {
            if (key === current)
                return Object.assign({}, prev);
            return Object.assign(Object.assign({}, prev), fallBackObj[current]);
        }, {});
        return !Object.keys(fallBackObj[key]).some((el) => restDimensions[el]);
    });
    return fallBackArr.some(Boolean);
};

const parseValue = (v) => { var _a; return parseInt(((_a = v.match(/\d+/)) === null || _a === void 0 ? void 0 : _a[0]) || 0, 10); };
const getDimensionName = (key, dimensions) => {
    const dim1Values = new Set();
    const dim2Values = new Set();
    Object.values(dimensions)
        .flat()
        .forEach((item) => {
        var _a;
        dim1Values.add(item.dimension1.variantName.toLowerCase());
        dim2Values.add((_a = item.dimension2) === null || _a === void 0 ? void 0 : _a.variantName.toLowerCase());
    });
    const lowerKey = key.toLowerCase();
    if (dim1Values.has(lowerKey) && !dim2Values.has(lowerKey)) {
        return 'dimension2';
    }
    return 'dimension1';
};
const findClosestVariant = (variantKey, dimensions, otherDimensionLabel) => {
    const variants = dimensions[variantKey];
    const target = parseValue(otherDimensionLabel);
    const dimensionName = getDimensionName(variantKey, dimensions);
    if (!target || !dimensionName) {
        return variants[0];
    }
    const sorted = variants
        .map((v) => {
        var _a;
        return (Object.assign(Object.assign({}, v), { value: parseValue((_a = v[dimensionName]) === null || _a === void 0 ? void 0 : _a.variantName) }));
    })
        .sort((a, b) => b.value - a.value);
    let closest = sorted[0];
    let minDiff = Math.abs(sorted[0].value - target);
    sorted.forEach((item, i) => {
        if (i === 0)
            return;
        const diff = Math.abs(item.value - target);
        if (diff < minDiff) {
            minDiff = diff;
            closest = item;
        }
    });
    return closest;
};

const checkVariantAvailability = (resultDimension, resultArray, variantDimension, otherDimensionLabel) => {
    const { fitable, tooltip } = variantDimension !== null && variantDimension !== void 0 ? variantDimension : {};
    Object.keys(resultDimension).forEach((key) => {
        const check = resultArray.some(({ dimensionLabel, disabled }) => {
            if (disabled)
                return true;
            const normalizedKey = key.toLowerCase();
            return normalizedKey === (dimensionLabel === null || dimensionLabel === void 0 ? void 0 : dimensionLabel.toLowerCase());
        });
        if (!check) {
            if (fitable) {
                resultArray.push({
                    dimensionLabel: key.charAt(0).toUpperCase() + key.slice(1),
                    href: findClosestVariant(key, resultDimension, otherDimensionLabel)
                        .href,
                    tooltip,
                    fitable,
                });
            }
            else {
                resultArray.push({ dimensionLabel: key, disabled: true });
            }
        }
    });
};

const mapVariantDimensions = (variantDimension, dimension) => variantDimension.map((val) => {
    var _a, _b, _c;
    return ({
        dimensionLabel: (_b = (_a = val[dimension]) === null || _a === void 0 ? void 0 : _a.variantName) !== null && _b !== void 0 ? _b : '',
        href: val.href,
        variantIcon: (_c = val[dimension]) === null || _c === void 0 ? void 0 : _c.variantIcon,
    });
});

const normalizeDimensions = (variants) => variants.reduce((prev, current) => {
    var _a, _b;
    const [first, second] = prev;
    const { dimension1, dimension2 } = current;
    const dimension1Key = (_a = dimension1.variantName) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    const dimension2Key = (_b = dimension2 === null || dimension2 === void 0 ? void 0 : dimension2.variantName) === null || _b === void 0 ? void 0 : _b.toLowerCase();
    return [
        Object.assign(Object.assign({}, first), { [dimension1Key]: [...(first[dimension1Key] || []), current] }),
        Object.assign(Object.assign({}, second), { [dimension2Key]: [...(second[dimension2Key] || []), current] }),
    ];
}, [{}, {}]);

const useVariants = ({ currentDimension1, currentDimension2, variant1Dimension, variant2Dimension, variants, }) => {
    const isOneDimension = useMemo(() => !currentDimension2 || !variants.every(({ dimension2 }) => dimension2), [variants, currentDimension2]);
    const isFallBack = useMemo(() => {
        if (isOneDimension)
            return false;
        return checkIsFallBack(variants);
    }, [isOneDimension, variants]);
    const prepareDimensions = useCallback(() => {
        var _a, _b, _c, _d;
        const [resultDimension1, resultDimension2] = normalizeDimensions(variants);
        const availableDimension1 = (_b = resultDimension2[(_a = currentDimension2 === null || currentDimension2 === void 0 ? void 0 : currentDimension2.toLowerCase()) !== null && _a !== void 0 ? _a : '']) !== null && _b !== void 0 ? _b : [];
        const availableDimension2 = (_d = resultDimension1[(_c = currentDimension1.toLowerCase()) !== null && _c !== void 0 ? _c : '']) !== null && _d !== void 0 ? _d : [];
        const result1 = mapVariantDimensions(availableDimension1, 'dimension1');
        const result2 = mapVariantDimensions(availableDimension2, 'dimension2');
        checkVariantAvailability(resultDimension1, result1, variant1Dimension, currentDimension2);
        checkVariantAvailability(resultDimension2, result2, variant2Dimension !== null && variant2Dimension !== void 0 ? variant2Dimension : {}, currentDimension1);
        return { dimension1: result1, dimension2: result2 };
    }, [
        currentDimension1,
        currentDimension2,
        variants,
        variant1Dimension,
        variant2Dimension,
    ]);
    const createFallbackArr = useCallback(() => variants.map(({ dimension1, dimension2, href }) => ({
        dimensionLabel: `${dimension1.variantName} ${dimension2 === null || dimension2 === void 0 ? void 0 : dimension2.variantName}`,
        href,
    })), [variants]);
    const [dimension1Arr, dimension2Arr] = useMemo(() => {
        if (isFallBack) {
            const dimension1 = createFallbackArr();
            return [dimension1, null];
        }
        if (!isOneDimension && !isFallBack) {
            const { dimension1, dimension2 } = prepareDimensions();
            return [dimension1, dimension2];
        }
        const mappedVariants = variants.map(({ dimension1, href }) => ({
            dimensionLabel: dimension1.variantName,
            variantIcon: dimension1.variantIcon,
            href,
        }));
        return [mappedVariants, null];
    }, [
        createFallbackArr,
        isFallBack,
        isOneDimension,
        prepareDimensions,
        variants,
    ]);
    return [dimension1Arr, dimension2Arr];
};

const useVariantMapper = ({ currentDimension1, currentDimension2, variant1Dimension, variant2Dimension, variants, singleVariant, }) => {
    const resolvedDimension1 = typeof currentDimension1 === 'object'
        ? currentDimension1.variantName
        : currentDimension1;
    const resolvedDimension2 = typeof currentDimension2 === 'object'
        ? currentDimension2.variantName
        : currentDimension2;
    const [dimension1Arr, dimension2Arr] = useVariants({
        currentDimension1: resolvedDimension1,
        currentDimension2: resolvedDimension2,
        variant1Dimension,
        variant2Dimension,
        variants,
    });
    const matchedDimension1Item = dimension1Arr.filter(({ dimensionLabel }) => dimensionLabel === resolvedDimension1);
    const variantsToDisplay = singleVariant
        ? matchedDimension1Item
        : dimension1Arr;
    return {
        dimension1Variants: variantsToDisplay,
        dimension2Variants: dimension2Arr,
        currentDimension1Label: resolvedDimension1,
        currentDimension2Label: resolvedDimension2,
    };
};

const VariantSelectorLinkList = forwardRef(({ variantsToDisplay, currentDimensionLabel }, ref) => (jsx("ul", Object.assign({ "aria-labelledby": "variant-list", className: "cds-variant-selector__list", ref: ref }, { children: variantsToDisplay === null || variantsToDisplay === void 0 ? void 0 : variantsToDisplay.map(({ href, dimensionLabel, variantIcon, disabled, tooltip, fitable }) => {
        const isSelected = dimensionLabel === currentDimensionLabel;
        return (jsx("li", { children: jsx(SelectorButton, Object.assign({ className: classNames({
                    'cds-variant-selector__disabled-option': disabled,
                }), href: href, isSelected: isSelected, tooltip: tooltip, fitable: fitable, "aria-disabled": disabled }, (isSelected ? { 'aria-current': true } : {}), { children: jsxs("span", Object.assign({ className: "cds-selector-button__inner" }, { children: [variantIcon ? jsx(ExtendableImage, Object.assign({}, variantIcon)) : null, dimensionLabel] })) })) }, href || dimensionLabel));
    }) }))));

const VariantSelectorList = forwardRef(({ variant1Dimension, variant2Dimension, currentDimension1, currentDimension2, variants, singleVariant, }, ref) => {
    const { dimension1Variants, dimension2Variants, currentDimension1Label, currentDimension2Label, } = useVariantMapper({
        currentDimension1,
        currentDimension2,
        variant1Dimension,
        variant2Dimension,
        variants,
        singleVariant,
    });
    return (jsxs("div", Object.assign({ className: "cds-variant-selector", ref: ref }, { children: [jsxs("div", Object.assign({ className: classNames('cds-variant-selector__block', {
                    'cds-variant-selector__block--one-dimension': !dimension2Variants,
                }) }, { children: [jsx(Typography, { id: "dimension1", content: variant1Dimension.label, variant: "body", size: "s", tag: "p" }), jsx(VariantSelectorLinkList, { variantsToDisplay: dimension1Variants, currentDimensionLabel: currentDimension1Label })] })), !singleVariant && variant2Dimension && !!dimension2Variants ? (jsxs("div", Object.assign({ className: "cds-variant-selector__block" }, { children: [jsx(Typography, { id: "dimension2", content: variant2Dimension.label, variant: "body", size: "s", tag: "p" }), jsx(VariantSelectorLinkList, { variantsToDisplay: dimension2Variants, currentDimensionLabel: currentDimension2Label })] }))) : null] })));
});

const VariantSelector = forwardRef(({ variant1Dimension, variant2Dimension, currentDimension1, currentDimension2, variants, singleVariant, productDataSku, multiVariant, }, ref) => multiVariant ? (jsx(MultiVariantSelectorList, { variants: variants, label: variant1Dimension.label, productDataSku: productDataSku, ref: ref })) : (jsx(VariantSelectorList, { variant1Dimension: variant1Dimension, variant2Dimension: variant2Dimension, currentDimension1: currentDimension1, currentDimension2: currentDimension2, variants: variants, singleVariant: singleVariant, ref: ref })));

export { VariantSelector };
